import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/shared/auth/AuthContext";
import {
  getRememberedUsername,
  createBrowserTokenStore,
} from "@/shared/auth/tokenStore";
import { isApiClientError } from "@/shared/api/errors";
import { landingPathForUser } from "@/modules/auth/domain/landing";
import { canAccessPath } from "@/modules/auth/domain/shellAccess";
import {
  Banner,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  Modal,
} from "@/shared/ui";

const schema = z.object({
  username: z.string().min(1, "Nhập tên đăng nhập"),
  password: z.string().min(1, "Nhập mật khẩu"),
  rememberMe: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const rememberedUsername = getRememberedUsername();
const defaultRememberMe = createBrowserTokenStore().getPersistPreference();

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      username: rememberedUsername || "",
      password: "",
      rememberMe: defaultRememberMe || Boolean(rememberedUsername),
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const user = await login(values.username.trim(), values.password, {
        rememberMe: values.rememberMe,
      });
      const from = (location.state as { from?: string } | null)?.from;
      const target =
        from && from !== "/auth/login" && canAccessPath(user.roles, from)
          ? from
          : landingPathForUser(user.roles);
      navigate(target, { replace: true });
    } catch (e) {
      if (isApiClientError(e)) {
        setFormError(e.message);
      } else {
        setFormError("Không kết nối được máy chủ.");
      }
    }
  });

  return (
    <>
      <Card className="w-full max-w-[460px] p-6 sm:p-7 shadow-lg border border-border/80">
        {/* Brand Logo & Header */}
        <div className="flex flex-col items-center gap-3 text-center pb-1">
          <img
            src="/logo/Logo.jpg"
            alt="Galaxy One"
            className="h-12 w-auto max-w-[15rem] object-contain"
          />
          <h1 className="m-0 text-page-title font-bold text-text">Đăng nhập</h1>
        </div>

        <form className="mt-5 space-y-4" onSubmit={onSubmit} noValidate>
          {formError && (
            <Banner
              tone="danger"
              title="Đăng nhập thất bại"
              description={formError}
              className="text-caption"
            />
          )}

          <FormField
            label="Tên đăng nhập"
            htmlFor="username"
            required
            error={errors.username?.message}
          >
            <Input
              id="username"
              size="lg"
              autoComplete="username"
              autoFocus={!rememberedUsername}
              placeholder="Nhập tên đăng nhập..."
              {...register("username")}
            />
          </FormField>

          <FormField
            label="Mật khẩu"
            htmlFor="password"
            required
            error={errors.password?.message}
          >
            <Input
              id="password"
              size="lg"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              autoFocus={Boolean(rememberedUsername)}
              placeholder="Nhập mật khẩu..."
              endAdornment={
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  tabIndex={-1}
                  className="text-text-muted hover:text-text cursor-pointer transition-colors p-1"
                  aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                >
                  {showPassword ? (
                    <EyeOff className="size-4.5" />
                  ) : (
                    <Eye className="size-4.5" />
                  )}
                </button>
              }
              {...register("password")}
            />
          </FormField>

          <div className="flex items-center justify-between pt-1">
            <Controller
              name="rememberMe"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="rememberMe"
                  size="lg"
                  label="Ghi nhớ đăng nhập"
                  checked={field.value}
                  onCheckedChange={(v) => field.onChange(v === true)}
                  onBlur={field.onBlur}
                  ref={field.ref}
                />
              )}
            />

            <button
              type="button"
              onClick={() => setShowForgotModal(true)}
              className="text-body font-medium text-primary hover:underline hover:text-primary-hover transition-colors cursor-pointer"
            >
              Quên mật khẩu?
            </button>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full text-white font-bold cursor-pointer mt-2 shadow-xs"
            loading={isSubmitting}
          >
            Đăng nhập
          </Button>
        </form>
      </Card>

      {/* Forgot Password Modal */}
      <Modal
        open={showForgotModal}
        onOpenChange={setShowForgotModal}
        title="Hỗ trợ khôi phục mật khẩu"
        size="sm"
      >
        <div className="space-y-3 text-caption text-text">
          <p className="m-0">
            Để đảm bảo tính an toàn cho hệ thống ERP nội bộ của TM Food, tài khoản nhân viên không hỗ trợ tự đặt lại mật khẩu qua email công cộng.
          </p>
          <div className="rounded-control bg-bg/80 border border-border p-3 space-y-1.5 text-[12px]">
            <div className="font-semibold text-text">Liên hệ bộ phận CNTT:</div>
            <div className="text-text-secondary">
              Email: <span className="font-mono text-primary font-medium">it@tmfood.vn</span>
            </div>
            <div className="text-text-secondary">
              Hotline nội bộ: <span className="font-mono text-primary font-medium">Ext. 102 (IT Support)</span>
            </div>
          </div>
          <p className="m-0 text-text-muted text-[11px]">
            Vui lòng cung cấp Họ tên và Mã nhân viên để được cấp lại mật khẩu tạm thời.
          </p>
        </div>
      </Modal>
    </>
  );
}
